import React, {
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { flowRight, truncate } from 'lodash';
import moment from 'moment';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  fetchChatAction,
  fetchQuizQuestionsAction,
  handleChatClickAction,
} from '../../components/app/app-state-actions.utilities';
import { RoutePath } from '../../components/app/app.constants';
import {
  ELSDate,
  ELSDropDown,
  ELSDropDownOption,
  ELSIcon,
  ELSPaginator,
  ELSTextBox,
  ELSTooltip,
  validateAfterDate,
  validateBeforeDate,
} from '../../components/els.components';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { QuizQuestionsPanel } from '../../components/quiz-questions-panel/QuizQuestionsPanel.component';
import { QuizPanelMode } from '../../components/quiz-questions-panel/quiz-questions-panel.constants';
import {
  ChatDto,
  QuizQuestionListItemDto,
  QuizQuestionListResponseDto,
  QuizQuestionSelectionDto,
  QuizQuestionSortDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { DATE_PRIMARY } from '../../constants/date.constants';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';
import { RecordEmpty } from '../../models/common.models';
import { preserveAdminOverrideParam } from '../../utilities/app.utilities';
import { convertMarkdownToPlainText } from '../../utilities/common.utilities';
import {
  getBookTitleWithEdition,
  getChatContextsForRequest,
  getOrderedPurchasedEntitlements,
  isShowQuizQuestionsNav,
  toastApiError
} from '../ai-chat/ai-chat.utilities';
import {
  ALL_BOOKS,
  QUIZ_QUESTIONS_PAGE_SIZE,
  QUIZ_QUESTIONS_SEARCH_DEBOUNCE_MS,
  QUIZ_QUESTIONS_SORT_OPTIONS,
  SEGMENT_SIZE_ALL,
} from './quiz-questions.constants';
import {
  getSegmentOptions,
  getSegmentSizeOptions,
  getSegmentWindow,
} from './quiz-questions.utilities';

type QuizQuestionsPanelContent = {
  mode: QuizPanelMode;
  selection: QuizQuestionSelectionDto;
  // How many questions the selection held when the panel opened, for the panel's analytics
  questionCount: number;
};

type QuizQuestionChatFilter = {
  chatId: number;
  title: string;
};

type QuizQuestionEntryFilter = {
  entryId: number;
  text: string;
};

type QuizQuestionsState = {
  searchInput: string;
  // Debounced searchInput, the value actually sent to NACS
  search: string;
  isbn: string;
  // Set from a question's options menu; shown as a removable filter pill
  chatFilter: QuizQuestionChatFilter;
  // Shows or hides the book, date and number of questions filters; the search and the chat and entry pills always stay visible
  isFiltersOpen: boolean;
  // The row whose options menu is open. Keyed by row, not one shared flag, because opening one menu
  // closes the one that was open and a shared flag would close the new menu with it.
  openMenuQuestionId: number;
  // Set from a question's options menu; shown as a removable filter pill
  entryFilter: QuizQuestionEntryFilter;
  createdFrom: Date;
  createdTo: Date;
  // Typed text that is not a date: the filter is not applied, so the box says why
  isCreatedFromInvalid: boolean;
  isCreatedToInvalid: boolean;
  // Remounts the date boxes, which keep their own text, when Clear filters empties them
  datesKey: number;
  sort: QuizQuestionSortDto;
  // SEGMENT_SIZE_ALL, or a number of questions per segment
  segmentSize: string;
  // Zero-based index of the chosen segment of that size
  segment: string;
  // 0-based, as NACS pages are
  page: number;
  response: QuizQuestionListResponseDto;
  // getResultsKey of the request the response above came from, or the one that failed
  resultsKey: string;
  panelContent: QuizQuestionsPanelContent;
  // Remounts the panel for every View/Take click, so each opens fresh
  panelKey: number;
};

const defaultState: QuizQuestionsState = {
  searchInput: '',
  search: '',
  isbn: ALL_BOOKS,
  chatFilter: null,
  entryFilter: null,
  isFiltersOpen: false,
  openMenuQuestionId: null,
  createdFrom: null,
  createdTo: null,
  isCreatedFromInvalid: false,
  isCreatedToInvalid: false,
  datesKey: 0,
  sort: QuizQuestionSortDto.DESC,
  segmentSize: SEGMENT_SIZE_ALL,
  segment: '0',
  page: 0,
  response: null,
  resultsKey: null,
  panelContent: null,
  panelKey: 0,
};

// Changing a filter changes how many questions match, so the old page and segment no longer mean anything
const SELECTION_RESET: Partial<QuizQuestionsState> = {
  page: 0,
  segmentSize: SEGMENT_SIZE_ALL,
  segment: '0',
};

const isValidDate = (date: unknown): date is Date => date instanceof Date && !Number.isNaN(date.getTime());

// An empty box means no date filter, so only typed text that does not parse is invalid
const isInvalidDateInput = (date: unknown, { originalInput }: { originalInput?: unknown } = {}): boolean =>
  !isValidDate(date) && typeof originalInput === 'string' && originalInput.trim() !== '';

// ELSDate only re-runs its validators when its value prop changes, and typed junk leaves the date as null.
// Passing this stable invalid Date while the text is invalid makes the message show and clear on time.
const INVALID_DATE_VALUE = new Date(NaN);

// Typed text that is not a date leaves the filter off, so the box has to say so
const getInvalidDateValidator = (isInvalid: boolean) => () => ({
  id: 'invalidDate',
  content: 'Enter a valid date',
  isValid: !isInvalid,
});

// What the table, View and Take all use, so they always cover the same questions. NACS compares
// created_at with a plain inclusive range, so the student's local day bounds are sent as ISO-8601
// with their offset.
const getQuizQuestionSelection = (state: QuizQuestionsState): QuizQuestionSelectionDto => ({
  search: state.search || undefined,
  isbn: state.isbn || undefined,
  chatId: state.chatFilter?.chatId,
  entryId: state.entryFilter?.entryId,
  // UTC ("Z"), not a local offset: a "+hh:mm" offset loses its "+" on the way to NACS and is rejected with a 422
  createdFrom: state.createdFrom ? moment(state.createdFrom).startOf('day').toISOString() : undefined,
  createdTo: state.createdTo ? moment(state.createdTo).endOf('day').toISOString() : undefined,
  sort: state.sort,
  ...getSegmentWindow(state.segmentSize, state.segment),
});

// Results are only shown while the selection still matches the request that loaded them, so after a
// filter, sort or segment change the previous rows never render or enable View/Take for the new
// selection. Page is left out so the current page stays on screen while the next one loads.
const getResultsKey = (state: QuizQuestionsState): string => JSON.stringify(getQuizQuestionSelection(state));

export type QuizQuestionsProps = ELSPropsFromToastService;

export const QuizQuestionsComponent = (props: QuizQuestionsProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const history = useHistory();
  const [state, setState] = useState<QuizQuestionsState>(defaultState);
  const latestRequestIdRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);

  const mergeState = (newState: Partial<QuizQuestionsState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  // The page is only reachable while its nav item shows, so a bookmark or a flag-off user lands on the chat instead
  useEffect(() => {
    if (!isShowQuizQuestionsNav(appState)) {
      history.replace(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
    }
  }, [appState.featureFlagsGrouped, appState.courseSectionId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const search = state.searchInput.trim();
      setState((prevState) => (prevState.search === search ? prevState : { ...prevState, search, ...SELECTION_RESET }));
    }, QUIZ_QUESTIONS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [state.searchInput]);

  useEffect(() => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    const resultsKey = getResultsKey(state);
    const isCurrent = () => requestId === latestRequestIdRef.current;

    fetchQuizQuestionsAction({
      appState,
      appStateRef,
      setAppState,
      userRole: appState.chatUserRole,
      context: getChatContextsForRequest(appState),
      params: {
        ...getQuizQuestionSelection(state),
        page: state.page,
        pageSize: QUIZ_QUESTIONS_PAGE_SIZE,
      },
    })
      .then((response) => {
        if (isCurrent()) {
          mergeState({ response, resultsKey });
        }
      })
      .catch(() => {
        if (isCurrent()) {
          mergeState({ response: null, resultsKey });
          toastApiError(props.toastService);
        }
      });
  }, [
    state.search,
    state.isbn,
    state.chatFilter,
    state.entryFilter,
    state.createdFrom,
    state.createdTo,
    state.sort,
    state.segmentSize,
    state.segment,
    state.page,
    appState.chatUserRole,
  ]);

  // The list is its own scroll container, so a new page or selection should start at the top
  useEffect(() => {
    listRef.current?.scrollTo?.({ top: 0 });
  }, [state.page, state.resultsKey]);

  const openPanel = (mode: QuizPanelMode) => {
    setState((prevState) => ({
      ...prevState,
      panelContent: {
        mode,
        selection: getQuizQuestionSelection(prevState),
        questionCount: prevState.response?.totalItems,
      },
      panelKey: prevState.panelKey + 1,
    }));
  };

  const closePanel = () => {
    mergeState({ panelContent: null });
  };

  const handlePanelError = () => {
    toastApiError(props.toastService);
    closePanel();
  };

  const handleChatTitleClick = (question: QuizQuestionListItemDto) => {
    mergeState({
      chatFilter: { chatId: question.chatId, title: question.chatTitle },
      ...SELECTION_RESET
    });
  };

  const openChat = (chat: ChatDto) => {
    handleChatClickAction({
      appState,
      appStateRef,
      setAppState,
      chat,
      sendClickEvent: true,
      checkLoading: true,
      toastService: props.toastService,
    });
    history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
  };

  const handleOpenChatClick = (question: QuizQuestionListItemDto) => {
    const cachedChat = appState.chats?.find((item) => item.id === question.chatId);
    if (cachedChat || !appState.chats?.length) {
      // With no chats loaded yet the chat page loads them, so an id and title are enough to open it
      openChat(cachedChat ?? ({ id: question.chatId, title: question.chatTitle } as ChatDto));
      return;
    }
    // The chat is not in the loaded list, and the chat page needs it cached to continue the conversation
    fetchChatAction({ appState, appStateRef, setAppState, chatId: question.chatId })
      .then((chat) => {
        setAppState((prevState) => ({
          ...prevState,
          chats: [chat, ...(prevState.chats ?? []).filter((item) => item.id !== chat.id)],
        }));
        openChat(chat);
      })
      .catch(() => toastApiError(props.toastService));
  };

  // Resets every filter, the number of questions and the segment; the sort is kept
  const clearFilters = () => {
    setState((prevState) => ({
      ...prevState,
      searchInput: defaultState.searchInput,
      search: defaultState.search,
      isbn: defaultState.isbn,
      createdFrom: null,
      createdTo: null,
      isCreatedFromInvalid: false,
      isCreatedToInvalid: false,
      datesKey: prevState.datesKey + 1,
      chatFilter: null,
      entryFilter: null,
      ...SELECTION_RESET
    }));
  };

  const clearChatFilter = () => {
    mergeState({ chatFilter: null, ...SELECTION_RESET });
  };

  const handleEntryTextClick = (question: QuizQuestionListItemDto) => {
    mergeState({
      entryFilter: { entryId: question.entryId, text: question.entryText },
      ...SELECTION_RESET
    });
  };

  const clearEntryFilter = () => {
    mergeState({ entryFilter: null, ...SELECTION_RESET });
  };

  // A new size starts at its first segment; the previous segment index belonged to the old size
  const handleSegmentSizeChange = (segmentSize: string) => {
    mergeState({ segmentSize, segment: '0', page: 0 });
  };

  const bookOptions: ELSDropDownOption[] = [
    { name: 'All books', value: ALL_BOOKS },
    ...getOrderedPurchasedEntitlements(appState.entitlements)
      .filter(({ title }) => !!title)
      .map(({ isbn, title, edition }) => ({ name: getBookTitleWithEdition(title, edition), value: isbn })),
  ];

  // Apps without books (Nora, Osmosis AI, AI Coach) have nothing to choose besides "All books"
  const hasBookOptions = bookOptions.length > 1;

  const isResultsCurrent = state.resultsKey === getResultsKey(state);
  const response = isResultsCurrent ? state.response : null;
  const questionCount = response?.totalItems ?? 0;
  const canViewOrTake = questionCount > 0;
  // Segment choices are built from the matches before the window, which the response reports
  const totalMatches = state.response?.totalMatches ?? 0;
  const isSegmentSized = state.segmentSize !== SEGMENT_SIZE_ALL;

  const renderMenuAction = (
    question: QuizQuestionListItemDto,
    ariaLabel: string,
    label: string,
    handleClick: (question: QuizQuestionListItemDto) => void
  ): JSX.Element => (
    <div className="u-els-margin-bottom-1o2">
      <button
        type="button"
        className="u-els-anchorize"
        aria-label={ariaLabel}
        onClick={() => {
          mergeState({ openMenuQuestionId: null });
          handleClick(question);
        }}
      >
        {label}
      </button>
    </div>
  );

  const renderQuestionRows = (): JSX.Element => (
    <table className="c-els-table c-els-table--padding-1o2" aria-label="Quiz questions">
      <thead>
        <tr className="c-els-table__row">
          <th className="c-els-table__cell c-els-table__cell--head u-els-width-9o10">Question</th>
          <th className="c-els-table__cell c-els-table__cell--head u-els-width-1o10">
            <span className="u-els-hide-visually">Options</span>
          </th>
        </tr>
      </thead>
      <tbody className="c-els-table__body">
        {response.content.map((question) => (
          <tr key={question.id} className="c-els-table__row">
            <td className="c-els-table__cell">
              <div className="c-naa-quiz-questions__prompt">
                {convertMarkdownToPlainText(question.questionData?.qtiData?.prompt)}
              </div>
              <div>
                <small className="u-els-color-n8">{moment(question.createdAt).format(DATE_PRIMARY)}</small>
              </div>
            </td>
            <td className="c-els-table__cell c-naa-quiz-questions__row-menu">
              <ELSTooltip
                id={`quiz-question-menu-tooltip-${question.id}`}
                showClose={false}
                hide={state.openMenuQuestionId !== question.id}
                onOpenToolTip={() => mergeState({ openMenuQuestionId: question.id })}
                onCloseToolTip={() => setState((prevState) => (
                  prevState.openMenuQuestionId === question.id ? { ...prevState, openMenuQuestionId: null } : prevState
                ))}
                triggerAriaLabel={`Options for question: ${truncate(convertMarkdownToPlainText(question.questionData?.qtiData?.prompt), { length: 60 })}`}
                dialogAriaLabel="Quiz question options menu"
                content={(
                  <div>
                    {renderMenuAction(question, `Go to chat: ${question.chatTitle || 'Untitled chat'}`, 'Go to chat', handleOpenChatClick)}
                    {renderMenuAction(
                      question,
                      `Filter to all questions generated in this chat thread: ${question.chatTitle || 'Untitled chat'}`,
                      'Filter to all questions generated in this chat thread',
                      handleChatTitleClick
                    )}
                    {renderMenuAction(
                      question,
                      `Filter to all questions generated in this message${question.entryText ? `: ${question.entryText}` : ''}`,
                      'Filter to all questions generated in this message',
                      handleEntryTextClick
                    )}
                  </div>
                )}
              >
                <span className="c-naa-action-menu__ellipsis">
                  <ELSIcon size="1x1o4" prefix="gizmo" name="ellipsis" customClass="u-els-display-block" />
                </span>
              </ELSTooltip>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // The chat and entry pills are always shown, so only the filters hidden with the toggle count towards its badge
  const hiddenFilterCount = [hasBookOptions && state.isbn, state.createdFrom || state.createdTo, isSegmentSized].filter(Boolean).length;

  const hasFilters = !!(state.search || state.isbn || state.chatFilter || state.entryFilter
    || state.createdFrom || state.createdTo);
  // A chosen number of questions is cleared along with the filters, though it does not count as one
  const canClearFilters = hasFilters || isSegmentSized || state.isCreatedFromInvalid || state.isCreatedToInvalid;

  const renderResults = (): JSX.Element => {
    // `response` is already null while the results do not match the current selection
    if (!response) {
      return null;
    }
    if (!response.totalItems) {
      return hasFilters
        ? (
          <>
            <p>No quiz questions match your filters.</p>
            <ELSButton preventAutoPending type={ELSButtonType.LINK} onClick={clearFilters}>Clear filters</ELSButton>
          </>
        )
        : <p>No quiz questions yet. Go to a chat and ask for quiz questions to be generated, and they will show up here.</p>;
    }
    return renderQuestionRows();
  };

  const renderPaginator = (): JSX.Element => {
    if (!response?.totalItems) {
      return null;
    }
    return (
      <div className="c-naa-quiz-questions__pagination u-els-margin-top">
        <ELSPaginator
          id="quiz-questions-paginator"
          a11y={{
            name: 'Quiz questions pages',
            pagingUnitName: 'Page',
            morePagingUnit: 'More pages',
          }}
          currentPage={state.page + 1}
          totalResults={response.totalItems}
          resultsPerPage={QUIZ_QUESTIONS_PAGE_SIZE}
          onPageChange={(newPage: number) => mergeState({ page: newPage - 1 })}
        />
      </div>
    );
  };

  const renderFilterPill = (pill: { label: string; title: string; closeLabel: string; onClose: () => void }): JSX.Element => (
    <FlexLayout
      classes={['c-els-pill c-naa-quiz-questions__filter-pill']}
      modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}
    >
      <FlexItem>
        <span className="c-naa-quiz-questions__chat-title" title={pill.title}>
          {pill.label}
        </span>
      </FlexItem>
      <FlexItem>
        <button
          type="button"
          className="c-naa-quiz-questions__filter-pill-close-button u-els-anchorize u-els-debuttonize"
          onClick={pill.onClose}
        >
          <ELSIcon name="close" size="1o2" />
          <span className="u-els-hide-visually">{pill.closeLabel}</span>
        </button>
      </FlexItem>
    </FlexLayout>
  );

  const isDateRangeSet = !!state.createdFrom && !!state.createdTo;

  return (
    <AiChatBaseTemplate
      isPanelOpen={!!state.panelContent}
      panelContent={state.panelContent && (
        <div className="c-naa-panel">
          <QuizQuestionsPanel
            key={state.panelKey}
            mode={state.panelContent.mode}
            selection={state.panelContent.selection}
            questionCount={state.panelContent.questionCount}
            onComplete={closePanel}
            onError={handlePanelError}
            headerEnd={(
              <button type="button" className="u-els-anchorize" onClick={closePanel}>
                <ELSIcon name="close" size="1x" />
                <span className="u-els-hide-visually">Close quiz questions</span>
              </button>
            )}
          />
        </div>
      )}
    >
      <div className="c-naa-base-page c-naa-quiz-questions">
        <div className="c-naa-base-page__header">
          <div className="c-naa-base-page__inner">
            <h3>Quiz questions</h3>
          </div>
        </div>
        <div className="c-naa-base-page__body c-naa-quiz-questions__body">
          <div className="c-naa-base-page__inner c-naa-quiz-questions__inner">

            <div className="c-naa-quiz-questions__controls">
              <div>
                <FlexLayout
                  classes={['c-naa-quiz-questions__fields']}
                  modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.BOTTOM]}
                >
                  <FlexItem classes={['u-els-width-1o4 c-naa-quiz-questions__field--half']}>
                    <ELSTextBox
                      name="quizQuestionsSearch"
                      value={state.searchInput}
                      changeHandler={(e, searchInput: string) => mergeState({ searchInput })}
                    >
                      Search questions
                    </ELSTextBox>
                  </FlexItem>
                  <FlexItem classes={['u-els-width-1o4 c-naa-quiz-questions__field--half']}>
                    <ELSDropDown
                      name="quizQuestionsSort"
                      value={state.sort}
                      options={QUIZ_QUESTIONS_SORT_OPTIONS}
                      changeHandler={(e, sort: QuizQuestionSortDto) => mergeState({ sort, page: 0 })}
                    >
                      Sort
                    </ELSDropDown>
                  </FlexItem>
                  <FlexItem classes={['c-naa-quiz-questions__buttons']} modifiers={[FlexLayoutModifier.GROW]}>
                    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.RIGHT]}>
                      <FlexItem>
                        <div className="c-naa-quiz-questions__toggle-wrapper">
                          <ELSButton
                            preventAutoPending
                            type={ELSButtonType.LINK}
                            ariaExpanded={state.isFiltersOpen}
                            onClick={() => mergeState({ isFiltersOpen: !state.isFiltersOpen })}
                          >
                            <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.MIDDLE]}>
                              <FlexItem>
                                <ELSIcon name="filter" prefix="gizmo" size="1x" customClass="u-els-display-block" />
                              </FlexItem>
                              <FlexItem>
                                {state.isFiltersOpen ? 'Hide filters' : 'Show filters'}
                                {!state.isFiltersOpen && hiddenFilterCount > 0 && (
                                  <span className="u-els-hide-visually">{` (${hiddenFilterCount} applied)`}</span>
                                )}
                              </FlexItem>
                            </FlexLayout>
                          </ELSButton>
                          {!state.isFiltersOpen && hiddenFilterCount > 0 && (
                            <span className="c-naa-quiz-questions__toggle-badge" aria-hidden="true">{hiddenFilterCount}</span>
                          )}
                        </div>
                      </FlexItem>
                      {canClearFilters && (
                        <FlexItem>
                          <ELSButton preventAutoPending type={ELSButtonType.LINK} onClick={clearFilters}>
                            Clear filters
                          </ELSButton>
                        </FlexItem>
                      )}
                      <FlexItem>
                        <ELSButton
                          preventAutoPending
                          type={ELSButtonType.PRIMARY}
                          size={ELSButtonSize.SMALL}
                          isDisabled={!canViewOrTake}
                          onClick={() => openPanel(QuizPanelMode.VIEW)}
                        >
                          {`View questions (${questionCount})`}
                        </ELSButton>
                      </FlexItem>
                      <FlexItem>
                        <ELSButton
                          preventAutoPending
                          type={ELSButtonType.PRIMARY}
                          size={ELSButtonSize.SMALL}
                          isDisabled={!canViewOrTake}
                          onClick={() => openPanel(QuizPanelMode.TAKE)}
                        >
                          {`Take quiz (${questionCount})`}
                        </ELSButton>
                      </FlexItem>
                    </FlexLayout>
                  </FlexItem>
                </FlexLayout>
              </div>

              {state.isFiltersOpen && (
                <div className="c-naa-quiz-questions__actions">
                  <FlexLayout classes={['c-naa-quiz-questions__fields']} modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.WRAP]}>
                    {hasBookOptions && (
                      <FlexItem classes={['c-naa-quiz-questions__field--grow c-naa-quiz-questions__field--full']}>
                        <ELSDropDown
                          name="quizQuestionsBook"
                          value={state.isbn}
                          options={bookOptions}
                          changeHandler={(e, isbn: string) => mergeState({ isbn, ...SELECTION_RESET })}
                        >
                          Book
                        </ELSDropDown>
                      </FlexItem>
                    )}
                    <FlexItem classes={['c-naa-quiz-questions__field--grow c-naa-quiz-questions__field--half']}>
                      <ELSDate
                        key={`from-${state.datesKey}`}
                        name="quizQuestionsCreatedFrom"
                        value={state.isCreatedFromInvalid ? INVALID_DATE_VALUE : state.createdFrom}
                        disableAfter={state.createdTo}
                        changeHandler={(e, date: Date, details) => mergeState({
                          createdFrom: isValidDate(date) ? date : null,
                          isCreatedFromInvalid: isInvalidDateInput(date, details),
                          ...SELECTION_RESET
                        })}
                        validators={[
                          getInvalidDateValidator(state.isCreatedFromInvalid),
                          ...(isDateRangeSet ? [
                            validateBeforeDate(state.createdFrom, moment(state.createdTo).add(1, 'day'), {
                              message: 'Start date must be on or before the end date'
                            })
                          ] : [])
                        ]}
                      >
                        From
                      </ELSDate>
                    </FlexItem>
                    <FlexItem classes={['c-naa-quiz-questions__field--grow c-naa-quiz-questions__field--half']}>
                      <ELSDate
                        key={`to-${state.datesKey}`}
                        name="quizQuestionsCreatedTo"
                        value={state.isCreatedToInvalid ? INVALID_DATE_VALUE : state.createdTo}
                        disableBefore={state.createdFrom}
                        changeHandler={(e, date: Date, details) => mergeState({
                          createdTo: isValidDate(date) ? date : null,
                          isCreatedToInvalid: isInvalidDateInput(date, details),
                          ...SELECTION_RESET
                        })}
                        validators={[
                          getInvalidDateValidator(state.isCreatedToInvalid),
                          ...(isDateRangeSet ? [
                            validateAfterDate(state.createdTo, moment(state.createdFrom).subtract(1, 'day'), {
                              message: 'End date must be on or after the start date'
                            })
                          ] : [])
                        ]}
                      >
                        To
                      </ELSDate>
                    </FlexItem>
                    <FlexItem classes={['c-naa-quiz-questions__field--grow c-naa-quiz-questions__field--half']}>
                      <ELSDropDown
                        name="quizQuestionsSegmentSize"
                        value={state.segmentSize}
                        isDisabled={!isResultsCurrent}
                        options={getSegmentSizeOptions(totalMatches)}
                        changeHandler={(e, segmentSize: string) => handleSegmentSizeChange(segmentSize)}
                      >
                        Number of questions
                      </ELSDropDown>
                    </FlexItem>
                    {isSegmentSized && (
                      <FlexItem classes={['c-naa-quiz-questions__field--grow c-naa-quiz-questions__field--half']}>
                        <ELSDropDown
                          name="quizQuestionsSegment"
                          value={state.segment}
                          isDisabled={!isResultsCurrent}
                          options={getSegmentOptions(totalMatches, Number(state.segmentSize))}
                          changeHandler={(e, segment: string) => mergeState({ segment, page: 0 })}
                        >
                          Questions to include
                        </ELSDropDown>
                      </FlexItem>
                    )}
                  </FlexLayout>
                </div>
              )}

              {(state.chatFilter || state.entryFilter) && (
                <div className="u-els-margin-top">
                  <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP]}>
                    {state.chatFilter && renderFilterPill({
                      label: `Chat: ${state.chatFilter.title || 'Untitled chat'}`,
                      title: state.chatFilter.title,
                      closeLabel: 'Remove chat filter',
                      onClose: clearChatFilter,
                    })}
                    {state.entryFilter && renderFilterPill({
                      label: `Entry: ${state.entryFilter.text || 'Untitled message'}`,
                      title: state.entryFilter.text,
                      closeLabel: 'Remove entry filter',
                      onClose: clearEntryFilter,
                    })}
                  </FlexLayout>
                </div>
              )}

            </div>

            <div ref={listRef} className="c-naa-quiz-questions__list u-els-margin-top-2x">
              {renderResults()}
            </div>

            {renderPaginator()}
          </div>
        </div>
      </div>
    </AiChatBaseTemplate>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  withHTMLHeadSEO({ title: 'Quiz questions' }),
);

const QuizQuestions: React.FC<RecordEmpty> = enhance(QuizQuestionsComponent);

export default QuizQuestions;
